import { useRef, useState } from 'react';
import { TabsProps } from 'antd';
import { ProcessBlockConfigure } from './ConfigureProcessBlock';
import { ContentHeader } from 'components/ui/layout/ContentHeader';
import { useTranslation } from 'react-i18next';
import styles from './ProcessBlock.module.css';
import { DarwinTabs } from 'components/darwin-ui';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from 'routes/routes';
import { ConfigureDataCapture } from './ConfigureDataCapture';
import { CancelConfigModal } from './CancelConfigModal';

export interface TabSaveHandler {
  save: (saveAndExit?: boolean) => Promise<void>;
}

type TabKey = '1' | '2';

const TabWrapper = ({ children }: { children: JSX.Element }) => {
  return <div className={styles.tabWrapper}>{children}</div>;
};

export const ProcessBlock = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: traceabilityPlanId, tab } = useParams();

  //logic for handle tab
  const processBlockConfigureRef = useRef<TabSaveHandler>(null);
  const configureDataCaptureRef = useRef<TabSaveHandler>(null);
  const [activeTabKey, setActiveTabKey] = useState<TabKey>(
    (tab as TabKey) ?? ('1' as TabKey)
  );
  const [cancelConfig, setCancelConfig] = useState(false);
  const [currentName, setCurrentName] = useState('');

  //name for rendering name
  const handleNameChange = (name: string) => {
    setCurrentName(name);
  };

  const handleCancel = () => {
    navigate(
      generatePath(ROUTES.PROCESSES.MAPS.MAP.VIEW, {
        id: traceabilityPlanId || '',
      })
    );
  };

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: (
        <span className={styles.tabLabel}>
          {t('process-flow.configure.title')}
        </span>
      ),
      children: (
        <TabWrapper>
          <ProcessBlockConfigure
            ref={processBlockConfigureRef}
            onNameChange={handleNameChange}
          />
        </TabWrapper>
      ),
    },
    {
      key: '2',
      label: (
        <span className={styles.tabLabel}>
          {t('process-flow.data-capture.title')}
        </span>
      ),
      children: (
        <TabWrapper>
          <ConfigureDataCapture ref={configureDataCaptureRef} />
        </TabWrapper>
      ),
    },
  ];

  const handleSave = async () => {
    if (activeTabKey === '1' && processBlockConfigureRef.current) {
      await processBlockConfigureRef.current.save();
    } else if (activeTabKey === '2' && configureDataCaptureRef.current) {
      await configureDataCaptureRef.current.save();
    }
  };

  const handleSaveAndExit = async () => {
    if (activeTabKey === '1' && processBlockConfigureRef.current) {
      await processBlockConfigureRef.current.save(true);
    } else if (activeTabKey === '2' && configureDataCaptureRef.current) {
      await configureDataCaptureRef.current.save(true);
    }
  };

  return (
    <>
      <ContentHeader
        title={currentName || t('process-flow.title')}
        onCancel={() => setCancelConfig(true)}
        onSave={handleSave}
        onSaveTitle={t('process-flow.save-button')}
        displayExtraButton={true}
        extraButtonTitle={t('save-exit')}
        onExtraButtonClick={handleSaveAndExit}
      />

      <CancelConfigModal
        open={cancelConfig}
        onCancel={() => setCancelConfig(false)}
        handleConfirm={handleCancel}
      />
      <DarwinTabs
        defaultActiveKey="1"
        activeKey={activeTabKey}
        onChange={key => setActiveTabKey(key as TabKey)}
        size="large"
        items={items}
        className={styles.tabTitle}
      />
    </>
  );
};
